import { ago } from "../time";
import type { Contact, ContactGroup } from "../types/contact";

/**
 * The contacts fixture world: Sofia's Google Contacts and Dan's Outlook
 * mailbox, which is the only contacts source some tenants have.
 *
 * Two deliberate awkward cases. One contact is `inferred` — Aux0 has only ever
 * seen the address in a message, never in an address book, which is exactly
 * the row a "sync my contacts" feature must not write back to the provider.
 * One has two work emails and no primary flag agreement between its phones,
 * which is what a model with a single `email` string silently loses.
 */

export const CONTACT_GROUPS: ContactGroup[] = [
  {
    id: "grp_sofia_all",
    accountId: "acc_gn_sofia",
    name: "All contacts",
    system: true,
    count: 1844,
  },
  {
    id: "grp_sofia_customers",
    accountId: "acc_gn_sofia",
    name: "Customers",
    system: false,
    count: 311,
  },
  {
    id: "grp_sofia_investors",
    accountId: "acc_gn_sofia",
    name: "Investors",
    system: false,
    count: 24,
  },
  {
    id: "grp_dan_mailbox",
    accountId: "acc_ol_dan",
    name: "Mailbox contacts",
    system: true,
    count: 96,
  },
];

export const CONTACTS: Contact[] = [
  {
    id: "con_sofia_omar",
    accountId: "acc_gn_sofia",
    provider: "google-contacts",
    providerId: "people/c4471920233",
    name: "Omar Haddad",
    givenName: "Omar",
    familyName: "Haddad",
    emails: [
      { address: "omar.haddad@contoso.com", label: "work", primary: true },
      { address: "omar@sablehq.com", label: "work", primary: false },
    ],
    phones: [
      { number: "+44 7700 900 182", label: "mobile", primary: true },
      { number: "+44 20 7946 0991", label: "work", primary: false },
    ],
    organizations: [
      { name: "Contoso", title: "Director of Revenue Operations" },
      { name: "SableHQ", title: "Advisor", department: "Board" },
    ],
    groupIds: ["grp_sofia_all", "grp_sofia_customers"],
    note: "Renewal in November. Owns the Salesforce side; wants the sync audited before signature.",
    starred: true,
    inferred: false,
    createdAt: ago(16, "w"),
    updatedAt: ago(2, "d"),
  },
  {
    id: "con_sofia_maya",
    accountId: "acc_gn_sofia",
    provider: "google-contacts",
    providerId: "people/c1180029114",
    name: "Maya Rao",
    givenName: "Maya",
    familyName: "Rao",
    emails: [{ address: "maya@northwind.io", label: "work", primary: true }],
    phones: [{ number: "+44 7700 900 455", label: "mobile", primary: true }],
    organizations: [{ name: "Northwind", title: "Head of Sales, EMEA" }],
    groupIds: ["grp_sofia_all", "grp_sofia_customers"],
    starred: false,
    inferred: false,
    createdAt: ago(11, "w"),
    updatedAt: ago(11, "w"),
  },
  {
    id: "con_sofia_assessor",
    accountId: "acc_gn_sofia",
    provider: "google-contacts",
    providerId: "people/c9922014477",
    name: "CASA assessor",
    emails: [{ address: "assessor@casa-example.org", label: "work", primary: true }],
    phones: [],
    organizations: [{ name: "CASA", department: "Tier 2 assessments" }],
    groupIds: ["grp_sofia_all"],
    note: "Scope justifications and retention settings before the evidence call.",
    starred: false,
    inferred: false,
    createdAt: ago(3, "w"),
    updatedAt: ago(4, "h"),
  },
  {
    id: "con_sofia_lena",
    accountId: "acc_gn_sofia",
    provider: "google-contacts",
    providerId: "people/c3380119822",
    name: "Lena Fischer",
    givenName: "Lena",
    familyName: "Fischer",
    emails: [
      { address: "lena@northwind.io", label: "work", primary: true },
      { address: "lena@fischer.dev", label: "home", primary: false },
    ],
    phones: [{ number: "+49 151 2345 6789", label: "mobile", primary: true }],
    organizations: [{ name: "Northwind", title: "Staff Engineer" }],
    groupIds: ["grp_sofia_all"],
    starred: false,
    inferred: false,
    createdAt: ago(21, "w"),
    updatedAt: ago(6, "w"),
  },
  {
    id: "con_sofia_investor",
    accountId: "acc_gn_sofia",
    provider: "google-contacts",
    providerId: "people/c7710228455",
    name: "Priya Shah",
    givenName: "Priya",
    familyName: "Shah",
    emails: [{ address: "priya@northwind.io", label: "work", primary: true }],
    phones: [],
    organizations: [{ name: "Northwind", title: "Founder" }],
    groupIds: ["grp_sofia_all", "grp_sofia_investors"],
    starred: true,
    inferred: false,
    createdAt: ago(21, "w"),
    updatedAt: ago(14, "w"),
  },
  {
    /**
     * Never in an address book: Aux0 derived it from message headers. It is in
     * autocomplete and nowhere else, and the UI says so.
     */
    id: "con_inferred_trust",
    accountId: "acc_gn_sofia",
    provider: "google-contacts",
    providerId: "inferred/trust@aux0.com",
    name: "Aux0 Trust",
    emails: [{ address: "trust@aux0.com", primary: true }],
    phones: [],
    organizations: [],
    groupIds: [],
    starred: false,
    inferred: true,
    createdAt: ago(9, "d"),
    updatedAt: ago(9, "d"),
  },
  {
    id: "con_dan_aisha",
    accountId: "acc_ol_dan",
    provider: "outlook-contacts",
    providerId: "AAMkADk4MjJiNWRl",
    name: "Aisha Kone",
    givenName: "Aisha",
    familyName: "Kone",
    emails: [{ address: "aisha@northwind.io", label: "work", primary: true }],
    phones: [{ number: "+33 6 12 34 56 78", label: "mobile", primary: true }],
    organizations: [{ name: "Northwind", title: "Finance" }],
    groupIds: ["grp_dan_mailbox"],
    starred: false,
    inferred: false,
    createdAt: ago(6, "w"),
    updatedAt: ago(6, "w"),
  },
  {
    id: "con_dan_tom",
    accountId: "acc_ol_dan",
    provider: "outlook-contacts",
    providerId: "AAMkADk4MjJiNWUy",
    name: "Tom Okafor",
    givenName: "Tom",
    familyName: "Okafor",
    emails: [
      { address: "tom@northwind.io", label: "work", primary: true },
      { address: "tom.okafor@icloud.com", label: "home", primary: false },
    ],
    phones: [{ number: "+44 7700 900 310", label: "mobile", primary: true }],
    organizations: [{ name: "Northwind", title: "Engineering" }],
    groupIds: ["grp_dan_mailbox"],
    note: "iCloud app password keeps expiring — send a reconnect link rather than debugging it.",
    starred: false,
    inferred: false,
    createdAt: ago(30, "w"),
    updatedAt: ago(3, "h"),
  },
];

export const CONTACT_BY_ID = new Map(CONTACTS.map((contact) => [contact.id, contact]));
